import type { BuildingDef } from './types';

/**
 * Buildings drive the management layer.
 * - drill: passive ore/s on the focused layer (consumes energy)
 * - smelter: converts raw ore -> ingots (consumes energy)
 * - powerplant: produces energy
 * - warehouse: raises storage cap
 * - elevator: required count gates how deep you can unlock
 * - lab: produces research points/s
 *
 * `energy` is +produced / -consumed per unit per second. `power` is the
 * kind-specific magnitude.
 */
export const BUILDINGS: readonly BuildingDef[] = [
  {
    id: 'drill', name: 'Auto-Bohrer', kind: 'drill', icon: '🛠️',
    description: 'Baut die fokussierte Schicht automatisch ab. Braucht Strom.',
    baseCost: 100, growth: 1.12, energy: -1, power: 0.5,
  },
  {
    id: 'powerplant', name: 'Kraftwerk', kind: 'powerplant', icon: '⚡',
    description: 'Erzeugt die Energie, nach der deine Maschinen gieren.',
    baseCost: 500, growth: 1.13, energy: 0, power: 10,
    requires: { lifetimeEarnings: 300 },
  },
  {
    id: 'smelter', name: 'Schmelzofen', kind: 'smelter', icon: '🔥',
    description: 'Veredelt Roherz zu Barren, die ein Vielfaches wert sind.',
    baseCost: 2_500, growth: 1.14, energy: -3, power: 0.4,
    requires: { lifetimeEarnings: 2_000, tech: 'smelting' },
  },
  {
    id: 'warehouse', name: 'Lagerhaus', kind: 'warehouse', icon: '🏭',
    description: 'Erhöht, wie viel Erz und Barren du lagern kannst.',
    baseCost: 1_500, growth: 1.15, energy: 0, power: 2_000,
    requires: { lifetimeEarnings: 1_000 },
  },
  {
    id: 'elevator', name: 'Aufzugschacht', kind: 'elevator', icon: '🛗',
    description: 'Jede Schacht-Stufe schaltet eine tiefere Schicht frei.',
    baseCost: 3_500, growth: 2.0, energy: -2, power: 1,
    requires: { lifetimeEarnings: 800 },
  },
  {
    id: 'lab', name: 'Forschungslabor', kind: 'lab', icon: '🔬',
    description: 'Erzeugt Forschungspunkte für den Technologiebaum.',
    baseCost: 12_000, growth: 1.16, energy: -2, power: 0.2,
    requires: { lifetimeEarnings: 8_000 },
  },
] as const;

export const BUILDING_BY_ID: Record<string, BuildingDef> = Object.fromEntries(
  BUILDINGS.map((b) => [b.id, b]),
);

/** Base storage capacity before any warehouses. */
export const BASE_STORAGE = 5_000;
